/**
 * Payment + Expense Ledger report.
 *
 * Unified chronological view of all money flows in the business:
 *   - Customer payments (invoicing payments in)
 *   - Supplier payments (payables payments out)
 *   - Loan repayments (outgoing loan debt service)
 *   - Operating expenses (cost side)
 *
 * KPI strip: Total Payments In, Total Payments Out, Total Expenses, Net Cash.
 * Filter chips: All / Customer Payments / Supplier Payments / Loan Payments / Expenses.
 * Direction column uses green arrow (in) / red arrow (out).
 */

import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Wallet } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";
import { Badge, DataTable, KpiCard, cn } from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockExpenses,
  mockLoanRepayments,
  mockLoans,
  type MockExpense,
  type MockLoan,
  type MockLoanRepayment,
} from "../../../lib/financeMock";
import { mockSupplierPayments, type MockSupplierPayment } from "../../../lib/payablesMock";
import { mockPayments, mockInvoices, type MockPayment, type MockInvoice } from "../../../lib/invoicingMock";
import { mockSuppliers } from "../../../components/imports/importsMockData";
import { mockDealers, type MockDealer } from "../../../lib/salesMock";
import {
  PeriodSelector,
  defaultPeriodWindow,
  type PeriodWindow,
} from "../../../components/gl/PeriodSelector";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  printElement,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";

export const Route = createFileRoute("/_app/reports/payment-expense-ledger")({
  component: PaymentExpenseLedgerPage,
});

/* ─────────────────────────────────────────────────────────────
 * Unified LedgerRow type
 * ───────────────────────────────────────────────────────────── */

type LedgerRowType =
  | "customer_payment"
  | "supplier_payment"
  | "loan_payment"
  | "expense";

type LedgerRow = {
  _id: string;
  date: number;
  type: LedgerRowType;
  /** Human-readable reference number */
  reference: string;
  /** Counterparty name (customer, supplier, bank, payee) */
  counterparty: string;
  direction: "in" | "out";
  amountInBaseCents: bigint;
  /** Original currency (may differ from BDT) */
  currency: string;
  amountCents: bigint;
  method: string;
  /** Void / voided / cancelled / pending etc */
  status: string;
};

/* ─────────────────────────────────────────────────────────────
 * Build unified ledger
 * ───────────────────────────────────────────────────────────── */

type BuildLedgerArgs = {
  fromDate: number;
  toDate: number;
  invoices: ReadonlyArray<MockInvoice>;
  payments: ReadonlyArray<MockPayment>;
  supplierPayments: ReadonlyArray<MockSupplierPayment>;
  dealers: ReadonlyArray<MockDealer>;
  suppliers: ReadonlyArray<{ _id: string; displayName: string }>;
  loans: ReadonlyArray<MockLoan>;
  loanRepayments: ReadonlyArray<MockLoanRepayment>;
  expenses: ReadonlyArray<MockExpense>;
};

function buildLedger(args: BuildLedgerArgs): LedgerRow[] {
  const { fromDate, toDate, invoices, payments, supplierPayments, dealers, suppliers, loans, loanRepayments, expenses } = args;
  const rows: LedgerRow[] = [];

  // Invoice map for dealer lookup via invoice → dealer
  const invoiceMap = new Map(invoices.map((i) => [i._id, i]));
  // Loan map for counterparty lookup
  const loanMap = new Map(loans.map((l) => [l._id, l]));

  // 1. Customer payments (direction: in)
  for (const p of payments) {
    const date = p.receivedAt;
    if (date < fromDate || date > toDate) continue;
    const invoice = invoiceMap.get(p.invoiceId);
    const dealer = invoice ? dealers.find((d) => d._id === invoice.dealerId) ?? null : null;
    const counterparty = dealer?.displayName ?? invoice?.dealerId ?? "Unknown";
    const status = p.voidedAt ? "Voided" : "Received";
    rows.push({
      _id: `ledger_cust_${p._id}`,
      date,
      type: "customer_payment",
      reference: p.paymentNumber,
      counterparty,
      direction: "in",
      amountInBaseCents: p.amountInBaseCents,
      currency: p.currency,
      amountCents: p.amountCents,
      method: p.paymentMethod,
      status,
    });
  }

  // 2. Supplier payments (direction: out)
  for (const p of supplierPayments) {
    const date = p.occurredAt;
    if (date < fromDate || date > toDate) continue;
    const supplier = suppliers.find((s) => s._id === p.supplierId) ?? null;
    const counterparty = supplier?.displayName ?? p.supplierId;
    rows.push({
      _id: `ledger_supp_${p._id}`,
      date,
      type: "supplier_payment",
      reference: p.paymentNumber,
      counterparty,
      direction: "out",
      amountInBaseCents: p.amountInBaseCents,
      currency: p.currency,
      amountCents: p.amountCents,
      method: p.paymentMethod,
      status: "Paid",
    });
  }

  // 3. Loan repayments (direction: out — only outgoing loans are debt service)
  for (const r of loanRepayments) {
    const loan = loanMap.get(r.loanId);
    if (!loan) continue;
    if (loan.direction !== "outgoing") continue;
    const date = r.occurredAt;
    if (date < fromDate || date > toDate) continue;
    const amountInBase = (r.totalCents * loan.fxRateToBaseE8) / 100_000_000n;
    rows.push({
      _id: `ledger_loan_${r._id}`,
      date,
      type: "loan_payment",
      reference: r.repaymentNumber,
      counterparty: loan.counterpartyName,
      direction: "out",
      amountInBaseCents: amountInBase,
      currency: r.currency,
      amountCents: r.totalCents,
      method: r.paymentMethod,
      status: "Paid",
    });
  }

  // 4. Expenses (direction: out)
  for (const e of expenses) {
    if (e.status === "void" || e.status === "rejected") continue;
    const date = e.occurredAt;
    if (date < fromDate || date > toDate) continue;
    rows.push({
      _id: `ledger_exp_${e._id}`,
      date,
      type: "expense",
      reference: e.expenseNumber,
      counterparty: e.payeeName ?? e.description,
      direction: "out",
      amountInBaseCents: e.amountInBaseCents,
      currency: e.currency,
      amountCents: e.amountCents,
      method: e.paymentMethod,
      status:
        e.status === "paid"
          ? "Paid"
          : e.status === "approved"
            ? "Approved"
            : "Pending",
    });
  }

  // Sort newest first
  rows.sort((a, b) => b.date - a.date);
  return rows;
}

/* ─────────────────────────────────────────────────────────────
 * Filter chip config
 * ───────────────────────────────────────────────────────────── */

type FilterTab = "all" | LedgerRowType;

const FILTER_TABS: Array<{ key: FilterTab; label: string }> = [
  { key: "all", label: "All" },
  { key: "customer_payment", label: "Customer Payments" },
  { key: "supplier_payment", label: "Supplier Payments" },
  { key: "loan_payment", label: "Loan Payments" },
  { key: "expense", label: "Expenses" },
];

const TYPE_LABEL: Record<LedgerRowType, string> = {
  customer_payment: "Customer Payment",
  supplier_payment: "Supplier Payment",
  loan_payment: "Loan Payment",
  expense: "Expense",
};

const TYPE_VARIANT: Record<
  LedgerRowType,
  "info" | "warning" | "neutral" | "danger"
> = {
  customer_payment: "info",
  supplier_payment: "warning",
  loan_payment: "danger",
  expense: "neutral",
};

/* ─────────────────────────────────────────────────────────────
 * Page component
 * ───────────────────────────────────────────────────────────── */

function PaymentExpenseLedgerPage() {
  const [payments] = usePersistentMockState<MockPayment[]>("payments", mockPayments);
  const [invoices] = usePersistentMockState<MockInvoice[]>("invoices", mockInvoices);
  const [supplierPmts] = usePersistentMockState<MockSupplierPayment[]>("supplierPayments", mockSupplierPayments);
  const [dealers] = usePersistentMockState<MockDealer[]>("dealers", mockDealers);
  const [suppliers] = usePersistentMockState<typeof mockSuppliers>("suppliers", mockSuppliers);
  const [loans] = usePersistentMockState<MockLoan[]>("loans", mockLoans);
  const [loanRepayments] = usePersistentMockState<MockLoanRepayment[]>("loanRepayments", mockLoanRepayments);
  const [expenses] = usePersistentMockState<MockExpense[]>("expenses", mockExpenses);

  const [period, setPeriod] = useState<PeriodWindow>(defaultPeriodWindow());
  const [activeTab, setActiveTab] = useState<FilterTab>("all");
  const printRef = useRef<HTMLDivElement>(null);

  const allRows = useMemo(
    () => buildLedger({
      fromDate: period.fromDate,
      toDate: period.toDate,
      invoices,
      payments,
      supplierPayments: supplierPmts,
      dealers,
      suppliers,
      loans,
      loanRepayments,
      expenses,
    }),
    [period.fromDate, period.toDate, invoices, payments, supplierPmts, dealers, suppliers, loans, loanRepayments, expenses],
  );

  const filteredRows = useMemo(() => {
    if (activeTab === "all") return allRows;
    return allRows.filter((r) => r.type === activeTab);
  }, [allRows, activeTab]);

  /* ── KPIs ────────────────────────────────────────────────── */
  const totalIn = allRows
    .filter((r) => r.direction === "in")
    .reduce((s, r) => s + r.amountInBaseCents, 0n);

  const totalOut = allRows
    .filter((r) => r.direction === "out" && r.type !== "expense")
    .reduce((s, r) => s + r.amountInBaseCents, 0n);

  const totalExpenses = allRows
    .filter((r) => r.type === "expense")
    .reduce((s, r) => s + r.amountInBaseCents, 0n);

  const netCash = totalIn - totalOut - totalExpenses;

  /* ── CSV export ──────────────────────────────────────────── */
  function handleCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<LedgerRow>[] = [
      {
        key: "date",
        label: "Date",
        format: (v) => formatDateForCsv(Number(v)),
      },
      {
        key: "type",
        label: "Type",
        format: (v) => TYPE_LABEL[v as LedgerRowType],
      },
      { key: "reference", label: "Reference #" },
      { key: "counterparty", label: "Counterparty" },
      {
        key: "direction",
        label: "Direction",
        format: (v) => (v === "in" ? "In" : "Out"),
      },
      { key: "amountInBaseCents", label: "Amount (BDT)" },
      { key: "currency", label: "Currency" },
      { key: "amountCents", label: "Original Amount" },
      { key: "method", label: "Method" },
      { key: "status", label: "Status" },
    ];
    downloadCsv(
      exportFilename("payment-expense-ledger", extension),
      toCsv(filteredRows, columns),
    );
  }

  function handlePrint() {
    printElement(printRef.current, {
      title: "Payment + Expense Ledger",
      subtitle: `${formatDate(period.fromDate, { style: "short" })} → ${formatDate(period.toDate, { style: "short" })}`,
    });
  }

  /* ── Table columns ───────────────────────────────────────── */
  const columns = useMemo<ColumnDef<LedgerRow>[]>(
    () => [
      {
        id: "date",
        header: "Date",
        cell: ({ row }) => (
          <span className="tabular-nums text-[12px] text-(--text-secondary)">
            {formatDate(row.original.date, { style: "short" })}
          </span>
        ),
      },
      {
        id: "type",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant={TYPE_VARIANT[row.original.type]} size="sm">
            {TYPE_LABEL[row.original.type]}
          </Badge>
        ),
      },
      {
        id: "reference",
        header: "Reference #",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.reference}
          </span>
        ),
        meta: { mono: true },
      },
      {
        id: "counterparty",
        header: "Counterparty",
        cell: ({ row }) => (
          <span className="text-(--text-primary) truncate max-w-[180px] block">
            {row.original.counterparty}
          </span>
        ),
      },
      {
        id: "direction",
        header: "Direction",
        cell: ({ row }) =>
          row.original.direction === "in" ? (
            <span className="inline-flex items-center gap-1 text-(--success) font-semibold text-[12px]">
              <ArrowUp className="h-3.5 w-3.5" aria-hidden />
              In
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-(--danger) font-semibold text-[12px]">
              <ArrowDown className="h-3.5 w-3.5" aria-hidden />
              Out
            </span>
          ),
      },
      {
        id: "amount",
        header: "Amount",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <div className="text-right">
            <div
              className={cn(
                "font-mono tabular-nums text-[12px] font-semibold",
                row.original.direction === "in"
                  ? "text-(--success)"
                  : "text-(--text-primary)",
              )}
            >
              {formatMoney(row.original.amountInBaseCents, "BDT", {
                locale: "en-BD",
              })}
            </div>
            {row.original.currency !== "BDT" ? (
              <div className="text-[10px] text-(--text-tertiary) tabular-nums">
                {formatMoney(row.original.amountCents, row.original.currency, {
                  display: "code",
                  locale: "en-BD",
                })}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: "method",
        header: "Method",
        cell: ({ row }) => (
          <span className="text-[12px] text-(--text-secondary) capitalize">
            {row.original.method.replace(/_/g, " ")}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => {
          const s = row.original.status;
          const variant =
            s === "Paid" || s === "Received"
              ? ("success" as const)
              : s === "Approved"
                ? ("info" as const)
                : s === "Voided"
                  ? ("neutral" as const)
                  : ("warning" as const);
          return (
            <Badge variant={variant} size="sm">
              {s}
            </Badge>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-6" ref={printRef}>
      <PageHeader
        title="Payment + Expense Ledger"
        description={`All cash flows for ${formatDate(period.fromDate, { style: "short" })} → ${formatDate(period.toDate, { style: "short" })}.`}
        actions={
          <ExportButton
            onCsv={() => handleCsv("csv")}
            onExcel={() => handleCsv("xls")}
            onPrint={handlePrint}
          />
        }
      />

      <PeriodSelector value={period} onChange={setPeriod} />

      {/* KPI strip */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Cash flow KPIs"
      >
        <KpiCard
          caption="Total Payments In"
          value={formatMoneySmart(totalIn, "BDT", { locale: "en-BD" })}
          hint="Customer receipts in period"
        />
        <KpiCard
          caption="Total Payments Out"
          value={formatMoneySmart(totalOut, "BDT", { locale: "en-BD" })}
          hint="Supplier + loan payments out"
        />
        <KpiCard
          caption="Total Expenses"
          value={formatMoneySmart(totalExpenses, "BDT", { locale: "en-BD" })}
          hint="Operating expenses in period"
        />
        <KpiCard
          caption="Net Cash Movement"
          value={formatMoneySmart(
            netCash < 0n ? -netCash : netCash,
            "BDT",
            { locale: "en-BD" },
          )}
          hint={
            netCash >= 0n
              ? "Net inflow"
              : "Net outflow"
          }
        />
      </section>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTER_TABS.map((tab) => {
          const count =
            tab.key === "all"
              ? allRows.length
              : allRows.filter((r) => r.type === tab.key).length;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "h-7 rounded-full border px-2.5 text-[11px] transition-colors",
                activeTab === tab.key
                  ? "bg-(--accent) text-(--accent-fg) border-(--accent)"
                  : "bg-(--bg-canvas) text-(--text-secondary) border-(--border-subtle) hover:bg-(--bg-hover)",
              )}
            >
              {tab.label}
              {count > 0 ? (
                <span
                  className={cn(
                    "ml-1.5 rounded-full px-1 text-[10px]",
                    activeTab === tab.key
                      ? "bg-(--accent-fg) text-(--accent)"
                      : "bg-(--bg-surface) text-(--text-tertiary)",
                  )}
                >
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Main table */}
      <DataTable
        data={filteredRows}
        columns={columns}
        getRowId={(r) => r._id}
        emptyTitle="No transactions found"
        emptyDescription="No cash flows match this period and filter."
        emptyIcon={<Wallet className="h-5 w-5" aria-hidden />}
      />
    </div>
  );
}
